<script setup lang="ts">
  import { computed, ref } from 'vue'
  import TriangleIcon from '@/components/icons/TriangleIcon.vue'
  import {
    arrow, autoUpdate, offset, shift, useFloating
  } from '@floating-ui/vue'
  import { KsButton } from '@aschehoug/kloss'
  import RichTextRenderer from '@/components/common/RichTextRenderer.vue'
  import Typography from '@/components/common/Typography.vue'

  interface IGlossaryItem {
    explanation: string
    title?: string
  }

  export interface IGlossary {
    word?: string
    glossary?: IGlossaryItem
  }

  defineProps<IGlossary>()
  const isShowing = ref(false)
  const arrowElement = ref<HTMLElement | null>(null)
  const toggleElement = ref<HTMLElement | null>(null)
  const floatingElement = ref<HTMLElement | null>(null)

  const { floatingStyles, middlewareData } = useFloating(toggleElement, floatingElement, {
    whileElementsMounted: autoUpdate,
    middleware: [offset(16), shift(), arrow({ element: arrowElement })],
    placement: 'top'
  })

  const arrowX = computed(() => middlewareData.value.arrow?.x ?? null)
  const arrowY = computed(() => middlewareData.value.arrow?.y ?? null)

  const arrowStyles = computed(() => {
    return {
      left: arrowX.value !== null ? `${arrowX.value}px` : '',
      top: arrowY.value !== null ? `${arrowY.value}px` : '',
      ['bottom']: '-24px'
    }
  })

  function setFocusToActiveElement(element: HTMLElement): void {
    setTimeout(() => {
      element.focus()
    }, 100)
  }

  function toggleShowWordExplanation(): void {
    isShowing.value = !isShowing.value

    if (!isShowing.value || !floatingElement.value) return

    setFocusToActiveElement(floatingElement.value)
  }


  function handleKeyDownActiveDialog(event: KeyboardEvent):void {
    if(event.key === 'Escape') {
      isShowing.value = false
    }
  }

  function handleKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
    case 'Enter':
    case ' ':
      event.preventDefault()
      toggleShowWordExplanation()
      return
    case 'Escape':
      isShowing.value = false
      return
    }
  }
</script>

<template>
  <div
    v-if="isShowing"
    class="layover"
    data-testid="layover"
    @click="isShowing = false"
    @keydown="handleKeyDown"
  />
  <button
    ref="toggleElement"
    data-testid="glossary"
    class="explanation-toggler"
    aria-haspopup="dialog"
    data-track="ordforklaring-knapp"
    @click="toggleShowWordExplanation"
    @keydown="handleKeyDown"
  >
    <RichTextRenderer :rich-text="word" />
  </button>
  <div
    v-if="isShowing"
    ref="floatingElement"
    class="explanation-wrapper"
    role="dialog"
    aria-labelledby="explanation-title"
    aria-describedby="explanation-description"
    tabindex="0"
    :style="floatingStyles"
    @keydown="handleKeyDownActiveDialog"
  >
    <TriangleIcon
      ref="arrowElement"
      class="arrow"
      aria-hidden="true"
      :style="arrowStyles"
    />
    <div class="text-wrapper">
      <typography
        id="explanation-title"
        data-testid="explanation-title"
        variant="h4"
        class="title"
      >
        {{ glossary?.title }}
      </typography>
      <p
        id="explanation-description"
        data-testid="explanation-description"
        class="explanation"
      >
        {{ glossary?.explanation }}
      </p>
    </div>
    <KsButton
      variant="tertiary"
      size="small"
      shape="round"
      icon-right="xmark"
      aria-label="Lukk dialog"
      @click="isShowing = false"
    />
  </div>
</template>

<style lang="scss" scoped>
  .glossary {
    display: inline;
    margin: 0;
    position: relative; /* Added to set a positioning context for child elements */

    button {
      color: white;
      justify-content: center;
    }
  }

  .explanation-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    padding: 16px 24px 20px;
    background: var(--color-seagreen-10);
    color: var(--color-blue-50);
    border-radius: 8px;
    display: flex;
    gap: 24px;
    z-index: 20;
    max-width: 380px;

    .arrow {
      color: var(--color-seagreen-10);
      position: absolute;
      height: 23px;
      width: 23px;
      transform: translateX(-50%) translateY(-50%);
    }

    .text-wrapper {
      display: flex;
      flex-direction: column;
    }

    .title {
      margin: 0;
    }

    .explanation {
      margin: 0;
      font-size: 1rem;
    }

    button {
      color: var(--color-blue-50);
      padding: 0;
      top: 18px;
      right: 22px;
      width: 24px;
      height: 24px;
    }
  }

  .explanation-toggler {
    display: inline;
    margin: 0;
    cursor: pointer;
    color: var(--color-pair-color, var(--color-red-20));
    border: 0;
    font-family: inherit;
    font-size: inherit;
    text-decoration: underline;
    outline-offset: 21px;
    text-decoration-thickness: 2.5px;
    text-underline-offset: 5px;
    text-decoration-skip-ink: none;
    text-decoration-style: dotted;
    background: none;
    padding: 0;

    @include visible-focus-ring;
  }

  .layover {
    height: 100%;
    z-index: 10;
    background: #0000004D;
    position: fixed;
    top: 0;
    right: 50%;
    left: 50%;
    margin-left: -50%;
    margin-right: -50%;
    max-width: 100%;
  }
</style>

